import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';
import {
  isPrintRefusalCode,
  printRefusalCopy,
  type PrintRefusalDetail,
} from '@/lib/printRefusalErrors';

export interface PrintTicketStatus {
  id: string;
  /**
   * ALREADY_PRINTED — skipped by print-batch: a wristband prints once, a
   * further copy is a reprint (`reprintBatch`). NOT_PRINTED — skipped by
   * reprint-batch: a never-printed ticket takes a normal print. REFUSED — the
   * server would not print this ticket AT ALL (#1746: cancelled/refunded, or
   * another visit day); it is a CLIENT-side status derived from `errorCode`,
   * never a wire token, and unlike FAILED it must not offer a retry.
   */
  status:
    | 'PRINTED'
    | 'FAILED'
    | 'PENDING'
    | 'ALREADY_PRINTED'
    | 'NOT_PRINTED'
    | 'REFUSED'
    | string;
  message?: string;
  /** The server's machine-readable reason, kept so the UI can branch on it. */
  errorCode?: string | null;
}

/** Why the cashier is reprinting — required by the server, audited. */
export type ReprintReason = 'DAMAGED' | 'LOST' | 'PRINTER_JAM' | 'OTHER';

export interface ReprintInput {
  reason: ReprintReason;
  /** Required when reason is OTHER. */
  note?: string;
}

export interface PrintBatchResult {
  invoiceId: string;
  tickets: PrintTicketStatus[];
}

/** The wire shape `POST /qr-code/print-batch/:invoiceId` actually returns. */
interface RawPrintBatchRow extends PrintRefusalDetail {
  qrCodeId?: string;
  id?: string;
  value?: string;
  /** "success" | "failed" on the wire; the UI speaks PRINTED / FAILED. */
  status?: string;
  error?: string;
  message?: string;
  /**
   * Set on a refusal (#1746). The server reports it as `status: "failed"` +
   * this code rather than a new wire status the BO and the kiosk have never
   * heard of, so the code is the ONLY thing separating "this ticket may not be
   * printed" from "the printer jammed" — it must not be dropped here.
   */
  errorCode?: string;
}

/**
 * Normalise the batch-print response.
 *
 * The server answers `{ invoiceId, totalTickets, results: [{ qrCodeId, value,
 * status: "success"|"failed", error? }] }` — it has never sent a `tickets`
 * array, and its status tokens are lower-case. Every caller here reads
 * `res.tickets` and compares against `'PRINTED'` / `'FAILED'`, so an
 * un-normalised response threw `Cannot read properties of undefined (reading
 * 'filter')` the moment a cashier pressed "Cetak Semua Tiket" — the print
 * outcome, success or failure, never reached them at all.
 *
 * Translating here rather than at the two call sites keeps one author for the
 * mapping; a `tickets` array is still accepted so a server that later adopts
 * the UI's spelling needs no client change.
 */
function normalizePrintBatch(raw: unknown, invoiceId: string): PrintBatchResult {
  const payload = (raw ?? {}) as { invoiceId?: string; tickets?: unknown; results?: unknown };
  const rows: RawPrintBatchRow[] = Array.isArray(payload.tickets)
    ? (payload.tickets as RawPrintBatchRow[])
    : Array.isArray(payload.results)
      ? (payload.results as RawPrintBatchRow[])
      : [];

  return {
    invoiceId: payload.invoiceId ?? invoiceId,
    tickets: rows.map((r) => {
      const s = String(r.status ?? '').toLowerCase();
      // A refusal arrives as `failed` + a refusal `errorCode` (#1746). Lift it
      // to REFUSED here, once, so every surface stops offering a retry for a
      // ticket that can never print — and re-word the reason in Bahasa off the
      // row's own `visitDate` / `ticketStatus`, which is what makes a
      // MIXED-DATE invoice readable per ticket instead of "N gagal".
      const refused = isPrintRefusalCode(r.errorCode);
      const status = refused
        ? 'REFUSED'
        : s === 'success' || s === 'printed'
          ? 'PRINTED'
          : s === 'already_printed'
            ? 'ALREADY_PRINTED'
            : s === 'not_printed'
              ? 'NOT_PRINTED'
              : s
                ? 'FAILED'
                : 'PENDING';
      const serverMessage = r.error ?? r.message;
      return {
        id: String(r.qrCodeId ?? r.id ?? ''),
        status,
        message:
          (refused ? printRefusalCopy(r.errorCode, r, serverMessage) : null) ?? serverMessage,
        errorCode: r.errorCode ?? null,
      };
    }),
  };
}

/**
 * Print every never-printed wristband of an invoice. For a partner group
 * booking (ABT-S22) this IS the claim: `headcount` (people who arrived) prints
 * only that many; the server refuses a second claim (409 ALREADY_CLAIMED).
 */
export async function printBatch(invoiceId: string, opts: { headcount?: number } = {}): Promise<PrintBatchResult> {
  try {
    const res = await http.post<ApiEnvelope<unknown>>(
      `/qr-code/print-batch/${invoiceId}`,
      opts.headcount ? { headcount: opts.headcount } : {},
    );
    return normalizePrintBatch(unwrap(res.data), invoiceId);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function printSingle(qrId: string): Promise<PrintTicketStatus> {
  try {
    const res = await http.post<ApiEnvelope<PrintTicketStatus>>(`/qr-code/print/${qrId}`, {});
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Reprint every already-printed wristband on an invoice. Each one gets a NEW
 * QR value — the old wristband and the visitor's e-ticket stop working at the
 * gates. Ticketbox (cashier) only; the reason is audited.
 */
export async function reprintBatch(invoiceId: string, input: ReprintInput): Promise<PrintBatchResult> {
  try {
    const res = await http.post<ApiEnvelope<unknown>>(`/qr-code/reprint-batch/${invoiceId}`, input);
    return normalizePrintBatch(unwrap(res.data), invoiceId);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Reprint one already-printed wristband with a NEW QR value (see `reprintBatch`). */
export async function reprintSingle(qrId: string, input: ReprintInput): Promise<unknown> {
  try {
    const res = await http.post<ApiEnvelope<unknown>>(`/qr-code/reprint/${qrId}`, input);
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}
